import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Injectable, NgZone, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ComponentStore } from '@ngrx/component-store';
import { NoState } from '@siza-staff-care/shared/data';
import { Observable, tap } from 'rxjs';
import { AuthStore } from '../auth.store';
import { SessionTimeoutService } from '../session-timeout.service';

@Component({
	standalone: true,
	template: `
		<div cdkDrag cdkDragRootElement=".cdk-overlay-pane">
			<h1 mat-dialog-title cdkDragHandle>Session timeout</h1>
		</div>

		<div mat-dialog-content>
			<h2>Your Staff.Care session is about to expire because of inactivity.</h2>

			@if (sessionTimeoutService.vm$ | async; as vm) {
				<div class="ending">
					<div class="text">
						Your session will end in <span class="seconds">{{ vm.remainingSeconds }}</span> seconds
					</div>

					<mat-progress-spinner
						[diameter]="30"
						[value]="calculatePercent(vm.timeoutSeconds, vm.remainingSeconds) || vm.timeoutSeconds"
						color="warn"
					/>
				</div>
			}
		</div>

		<div mat-dialog-actions align="end">
			<button (click)="onResume()" mat-stroked-button color="primary">Resume</button>
			<button (click)="onLogout()" mat-stroked-button color="primary">Logout</button>
		</div>
	`,
	styles: [
		`
			:host {
				h2 {
					font-size: 1rem;
					margin: 0;
					margin-bottom: 12px;
				}

				.ending {
					display: flex;
					align-items: center;
					.text {
						flex: 1;
					}

					.seconds {
						font-weight: 500;
					}
				}
			}
		`,
	],
	imports: [CommonModule, DragDropModule, MatButtonModule, MatDialogModule, MatProgressSpinnerModule],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SessionTimeoutDialogComponent extends ComponentStore<NoState> {
	protected sessionTimeoutService = inject(SessionTimeoutService);
	private authStore = inject(AuthStore);
	private zone = inject(NgZone);

	constructor(public dialogRef: MatDialogRef<SessionTimeoutDialogComponent>) {
		super({});
		this.watchForSessionEnd(this.sessionTimeoutService.forceEnd$);
	}

	calculatePercent(timeoutSeconds: number, secondsLeft: number) {
		if (secondsLeft < 0) {
			secondsLeft = timeoutSeconds;
		}
		return (secondsLeft * 100) / timeoutSeconds;
	}

	onResume() {
		this.zone.run(() => {
			this.dialogRef.close();
			this.sessionTimeoutService.resume();
		});
	}

	onLogout() {
		this.zone.run(() => {
			this.dialogRef.close();
			this.authStore.logOut({ logoutFromTimeout: true });
		});
	}

	readonly watchForSessionEnd = this.effect((forceEnd$: Observable<boolean>) =>
		forceEnd$.pipe(
			tap((forceEnd) => {
				if (forceEnd) {
					this.authStore.logOut({ logoutFromTimeout: true, message: 'Session timeout' });
					this.dialogRef.close();
				}
			}),
		),
	);
}

@Injectable({
	providedIn: 'root',
})
export class SessionTimeoutDialogService extends ComponentStore<NoState> {
	constructor(private dialog: MatDialog) {
		super({});
	}

	readonly watchForSessionTimeoutWarning = this.effect((inWarning$: Observable<boolean>) =>
		inWarning$.pipe(
			tap((inWarning) => {
				if (inWarning) {
					this.show();
				}
			}),
		),
	);

	show() {
		return this.dialog
			.open(SessionTimeoutDialogComponent, {
				hasBackdrop: false,
			})
			.afterClosed();
	}
}
